import { isDebug } from "@/constants";
import { Context } from "@/context";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { RandomTrigger, Trigger } from "@/types";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

const matchesStop = (triggerStop: Trigger["triggerStop"], eventType: string) =>
  Array.isArray(triggerStop)
    ? triggerStop.includes(eventType)
    : triggerStop === eventType;

const matchesEvent = (trigger: Trigger, eventType: string) =>
  trigger.triggerStart === eventType ||
  matchesStop(trigger.triggerStop, eventType);

export const useParticleSystemsHandler = (
  id: string,
  particleSystemTriggers?: (Trigger | RandomTrigger)[],
  preview?: boolean,
) => {
  const { user: u } = useContext(Context);
  const user = u || JSON.parse(self.name).user;
  const { onDiceEvent, offDiceEvent } = useRenderedDiceStore();
  const previewIsParticlesEnabled = useUserDiceDataStore(
    (state) => state.getUserDiceData(user.id)?.settings.particlesEnabled,
  );
  const renderedIsParticlesEnabled = useRenderedDiceStore(
    (state) => state.userSettings?.particlesEnabled,
  );
  const isParticlesEnabled = preview
    ? previewIsParticlesEnabled
    : renderedIsParticlesEnabled;
  const [activeParticleSystems, setActiveParticleSystems] = useState<
    Set<string>
  >(new Set());
  const previousEvent = useRef<string | undefined>(undefined);

  const runParticlesForEvent = useCallback(
    (event?: string) => {
      if (!particleSystemTriggers || particleSystemTriggers.length === 0) {
        return;
      }

      // Only proceed if event type changes (unless preview)
      if (!preview && event === previousEvent.current) {
        return;
      }

      if (event === undefined) {
        setActiveParticleSystems(new Set());
        previousEvent.current = undefined;
        return;
      }

      previousEvent.current = event;

      // Gather potential particle triggers, resolving any random triggers. We
      // don't want to memo this because we want new random triggers every time.
      const resolvedTriggers = particleSystemTriggers.reduce<Trigger[]>(
        (acc, trigger) => {
          if (Array.isArray(trigger)) {
            const randomTrigger = trigger as RandomTrigger;
            const randomIndex = Math.floor(
              Math.random() * randomTrigger.length,
            );
            const randomSelection = randomTrigger[randomIndex];
            acc.push(
              ...(Array.isArray(randomSelection)
                ? randomSelection
                : [randomSelection]),
            );
          } else {
            acc.push(trigger);
          }
          return acc;
        },
        [],
      );

      const matchingTriggers = resolvedTriggers.filter((trigger) =>
        matchesEvent(trigger, event),
      );

      if ((isDebug || preview) && matchingTriggers.length > 0) {
        console.log(
          "particle systems triggered: ",
          matchingTriggers.map(({ definition, triggerStart, triggerStop }) => ({
            definition,
            triggerStart,
            triggerStop,
          })),
        );
      }

      setActiveParticleSystems((prevActive) => {
        const newActiveSet = new Set(prevActive);

        matchingTriggers.forEach(
          ({ definition, triggerStart, triggerStop }) => {
            if (matchesStop(triggerStop, event)) {
              // Some stop triggers listen to multiple events, so we need to check if
              // the definition is still active, and no other events have removed it
              if (newActiveSet.has(definition)) {
                if (isDebug) {
                  console.log("Stopping particle system:", definition);
                }
                newActiveSet.delete(definition);
              }
            } else if (triggerStart === event) {
              if (isDebug) {
                console.log("Starting particle system:", definition);
              }
              newActiveSet.add(definition);
            }
          },
        );

        return newActiveSet;
      });
    },
    [particleSystemTriggers, preview],
  );

  useEffect(() => {
    if (!isParticlesEnabled) return;

    const handleEvent = ({
      id: eventId,
      trigger,
    }: {
      id: string;
      trigger?: string;
    }) => {
      if (eventId !== id) return;

      runParticlesForEvent(trigger);
    };

    onDiceEvent?.("event", handleEvent);

    return () => {
      offDiceEvent?.("event", handleEvent);
    };
  }, [id, isParticlesEnabled, offDiceEvent, onDiceEvent, runParticlesForEvent]);

  return activeParticleSystems;
};
